import { BffApiClientMolecule } from "@/app/api-client/store"
import { getHighestCollectionOffersQueryParams } from "@/app/collections/offers/utils"
import type { Bid } from "@anaconda/core/bid/types"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import type { NftId, NftMiniature } from "@anaconda/core/nft/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { useQueries } from "@tanstack/react-query"
import Big from "big.js"
import { useMolecule } from "bunshi/react"
import { useMemo } from "react"

export interface OfferMatch {
  offersLoading: boolean
  offer?: Bid
}

interface SellState {
  offerByNft: Record<NftId, OfferMatch>
  readyToSell: boolean
  sellPrice?: PriceValue
  bestSellOffer: Bid | undefined
}

export const useSellState = (nftsToSell: NftMiniature[]): SellState => {
  const bffApiClient = useMolecule(BffApiClientMolecule)

  // Get unique collection IDs
  const uniqueCollectionIds = useMemo(() => {
    const ids = nftsToSell.map((nft) => nft.collection.id)
    return Array.from(new Set(ids))
  }, [nftsToSell])

  // Fetch offers for each collection
  const queries = useQueries({
    queries: uniqueCollectionIds.map((collectionId) => ({
      ...getHighestCollectionOffersQueryParams(bffApiClient, collectionId),
      refetchInterval: 5000,
    })),
  })

  // Map collection IDs to query results
  const collectionsOffers = useMemo(() => {
    const result: Record<CollectionId, (typeof queries)[number]> = {}
    uniqueCollectionIds.forEach((collectionId, index) => {
      result[collectionId] = queries[index]
    })
    return result
  }, [uniqueCollectionIds, queries])

  // Match offers to nfts any time nfts or offers changed
  const offerByNft = useMemo(() => {
    const collectionOffersAlreadyMatched: Record<CollectionId, number> = {}
    const matchResult: SellState["offerByNft"] = {}

    for (const nft of nftsToSell) {
      const collectionId = nft.collection.id
      const collectionOffersQuery = collectionsOffers[collectionId]

      // If there are no offers for this collection, skip it
      if (!collectionOffersQuery) continue

      // If the offers are loading, set the loading state
      if (collectionOffersQuery.isLoading) {
        matchResult[nft.id] = { offersLoading: true }
        continue
      }

      const alreadyMatchedOffers =
        collectionOffersAlreadyMatched[collectionId] ?? 0
      const nextOffer = collectionOffersQuery.data?.offers[alreadyMatchedOffers]

      // Increment the counter if there is an offer
      if (nextOffer) {
        collectionOffersAlreadyMatched[collectionId] = alreadyMatchedOffers + 1
      }

      matchResult[nft.id] = {
        offersLoading: false,
        offer: nextOffer,
      }
    }

    return matchResult
  }, [collectionsOffers, nftsToSell])

  const readyToSell = useMemo(() => {
    // Check if there are any actual offers (not just loading states)
    const hasActualOffers = Object.values(offerByNft).some(
      (match) => !match.offersLoading && match.offer !== undefined,
    )

    return hasActualOffers
  }, [offerByNft])

  const sellPrice = useMemo(() => {
    return Object.values(offerByNft).reduce<PriceValue | undefined>(
      (acc, { offer }) => {
        if (!offer) return acc

        if (!acc) return offer.price

        return Object.assign({}, acc, {
          amount: Big(acc.amount).add(offer.price.amount).toString(),
        })
      },
      undefined,
    )
  }, [offerByNft])

  const bestSellOffer = useMemo(() => {
    const result = Object.values(offerByNft).reduce<Bid | undefined>(
      (acc, { offer }) => {
        if (!offer) return acc
        if (!acc) return offer

        return offer.price.amount > acc.price.amount ? offer : acc
      },
      undefined,
    )

    return result
  }, [offerByNft])

  return useMemo(
    () => ({
      offerByNft,
      readyToSell,
      sellPrice,
      bestSellOffer,
    }),
    [offerByNft, readyToSell, sellPrice, bestSellOffer],
  )
}
